import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

const RESOURCES_PATH = '/resources';

interface ResourceLink {
  href: string;
  title: string;
  description: string;
}

interface ReadingPath {
  title: string;
  links: Array<{
    href: string;
    label: string;
  }>;
}

interface ResourceAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

function ResourceCard({ resource }: { resource: ResourceLink }) {
  return (
    <article className="acc-public-page-subcard">
      <h3>{resource.title}</h3>
      <p>{resource.description}</p>
      <div className="acc-public-page-actions">
        <PublicCtaLink href={resource.href} variant="ghost">
          {resource.title}
        </PublicCtaLink>
      </div>
    </article>
  );
}

export default function ResourcesPage() {
  const { t } = useTranslation('marketing');
  const guideResources = t('pages.resourcesHub.guides.items', { returnObjects: true }) as ResourceLink[];
  const trustResources = t('pages.resourcesHub.trust.items', { returnObjects: true }) as ResourceLink[];
  const audienceItems = t('pages.resourcesHub.audience.items', { returnObjects: true }) as string[];
  const readingPaths = t('pages.resourcesHub.readingPaths.paths', { returnObjects: true }) as ReadingPath[];
  const ctaActions = t('pages.resourcesHub.cta.actions', { returnObjects: true }) as ResourceAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.resourcesHub.meta.title')}
      metaDescription={t('pages.resourcesHub.meta.description')}
      canonicalPath={RESOURCES_PATH}
      eyebrow={t('pages.resourcesHub.hero.eyebrow')}
      title={t('pages.resourcesHub.hero.title')}
      intro={t('pages.resourcesHub.hero.intro')}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.resourcesHub.intro.title')}</h2>
        <p>{t('pages.resourcesHub.intro.body')}</p>
        <p>{t('pages.resourcesHub.intro.note')}</p>
      </section>

      <section className="acc-public-page-section" aria-labelledby="popular-accessibility-guides-title">
        <div>
          <h2 id="popular-accessibility-guides-title">{t('pages.resourcesHub.guides.title')}</h2>
          <p>{t('pages.resourcesHub.guides.body')}</p>
        </div>
        <div className="acc-public-page-grid">
          {guideResources.map((resource) => (
            <ResourceCard key={resource.href} resource={resource} />
          ))}
        </div>
      </section>

      <section className="acc-public-page-section" aria-labelledby="trust-product-resources-title">
        <div>
          <h2 id="trust-product-resources-title">{t('pages.resourcesHub.trust.title')}</h2>
          <p>{t('pages.resourcesHub.trust.body')}</p>
        </div>
        <div className="acc-public-page-grid">
          {trustResources.map((resource) => (
            <ResourceCard key={resource.href} resource={resource} />
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.resourcesHub.audience.title')}</h2>
        <p>{t('pages.resourcesHub.audience.body')}</p>
        <ul className="acc-public-page-list" aria-label={t('pages.resourcesHub.audience.ariaLabel')}>
          {audienceItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="suggested-reading-paths-title">
        <div>
          <h2 id="suggested-reading-paths-title">{t('pages.resourcesHub.readingPaths.title')}</h2>
          <p>{t('pages.resourcesHub.readingPaths.body')}</p>
        </div>
        <div className="acc-public-page-grid">
          {readingPaths.map((path) => (
            <article key={path.title} className="acc-public-page-subcard">
              <h3>{path.title}</h3>
              <ol className="acc-public-page-list">
                {path.links.map((link) => (
                  <li key={link.href}>
                    <PublicCtaLink href={link.href} variant="ghost">
                      {link.label}
                    </PublicCtaLink>
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.resourcesHub.disclaimer.title')}</h2>
        <p>{t('pages.resourcesHub.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="resources-cta-title">
        <div>
          <h2 id="resources-cta-title">{t('pages.resourcesHub.cta.title')}</h2>
          <p>{t('pages.resourcesHub.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
